/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
/**
 * UnifiedCourseCard
 *
 * @file UnifiedCourseCard.jsx
 * @author Anuja Gaikwad
 * @since 2.0.0
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton, Menu, MenuItem, Typography } from '@material-ui/core';
import { BUTTON_TYPES } from '../../common/constants';
import CustomCard from '../common/CustomCard';
import Icon from '../icons/Icon';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import Download from '../download/Download';

const UnifiedCourseCard = (props) => {
  const {
    course = {},
    book = {},
    classes = {},
    config = {},
    callback = {}
  } = props;

  const {
    showCourseTitle = true,
    showBookTitle = true,
    showBookCover = true,
    showTitleInfo = true,
    showDownload = false,
    showMetaData = true,
    showBookActions = true,
    showCourseMenu = true,
    showInviteStudents = true
  } = config;

  const { handleMenuOpenChange } = callback;
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
    handleMenuOpenChange?.();
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const renderTitleInfo = () => {
    if (!showTitleInfo || !book.titleInfo) {
      return null;
    }

    const { tooltip, callback: bookTitleInfoCallback, showHotspot = false } = book.titleInfo;

    return (
      <TooltipWrapper tooltip={tooltip || ''} placement="top">
        <IconButton
          className={classes.unifiedCoverInfoButton}
          onClick={() => {
            bookTitleInfoCallback?.();
          }}
          aria-haspopup="dialog"
        >
          {showHotspot && <div className={classes.hotspot} />}
          <Icon name="InfoIcon" className={classes.infoIcon} />
        </IconButton>
      </TooltipWrapper>
    );
  };

  const renderBookCover = () => {
    if (!showBookCover || !book.coverImage) {
      return null;
    }

    const { progress, status, onCancelClick, onBookClick } = book?.titleInfo || {};

    return (
      <div className={classes.unifiedCoverContainer}>
        {showDownload ? (
          <Download
            backgroundImage={book?.coverImage}
            progress={progress}
            downloadStatus={status}
            onCancelClick={onCancelClick}
            onClick={onBookClick}
            classes={{
              root: classes.unifiedDownload
            }}
          />
        ) : (
          <img src={book.coverImage} alt="Book cover" className={classes.unifiedCoverImage} />
        )}
        {renderTitleInfo()}
      </div>
    );
  };

  const renderMetaData = () => {
    if (!showMetaData || !course.metaData?.length) {
      return null;
    }

    const badgeItems = course.metaData.filter((meta) => meta.badge && meta.info);
    const dateItems = course.metaData.filter((meta) => !meta.badge && meta.info);

    if (!badgeItems.length && !dateItems.length) {
      return null;
    }

    return (
      <div className={classes.unifiedMetaRow}>
        {badgeItems.map((meta) => (
          <span key={meta.label || meta.info} className={classes.unifiedBadge}>
            {meta.info}
          </span>
        ))}
        {dateItems.length === 1 && (
          <Typography className={classes.unifiedDateRange} component="span">
            {dateItems[0].info}
          </Typography>
        )}
        {dateItems.length > 1 && (
          <Typography className={classes.unifiedDateRange} component="span">
            {dateItems[0].info} – {dateItems[1].info}
          </Typography>
        )}
      </div>
    );
  };

  const renderIconCtaButton = (action, key) => {
    if (!action?.callback || !action.text) {
      return null;
    }

    return (
      <Button
        key={key}
        className={classes.iconCtaButton}
        onClick={() => action.callback(action)}
        startIcon={action.iconName ? <Icon name={action.iconName} className={classes.icon} /> : null}
      >
        {action.text}
      </Button>
    );
  };

  const renderBookAction = (action) => {
    if (!action.callback) {
      return null;
    }

    if (action.type === BUTTON_TYPES.BUTTON) {
      return (
        <Button
          key={action.text}
          classes={{
            label: classes.secondaryButtonText,
            root: classes.secondaryButton,
            focusVisible: classes.focusVisible
          }}
          onClick={action.callback}
        >
          {action.text}
        </Button>
      );
    }

    if (!action.type && action.text) {
      return renderIconCtaButton(action, action.text);
    }

    return null;
  };

  const renderCourseMenu = () => {
    if (!course.courseMenu || !course.courseMenu.length) {
      return null;
    }

    return (
      <Menu
        anchorEl={menuAnchorEl}
        keepMounted
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        getContentAnchorEl={null}
        classes={{
          paper: classes.leftAccordionListMenuPaper,
          list: classes.courseMenuRoot
        }}
      >
        {course.courseMenu.map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => {
              handleMenuClose();
              item.callback(item);
            }}
            classes={{
              root: `${classes.courseMenuItem} ${item.divider ? classes.courseMenuItemDivider : ''}`
            }}
          >
            <Typography>{item.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    );
  };

  const renderSettingsMenuTrigger = () => {
    if (!showCourseMenu || !course.courseMenu?.length) {
      return null;
    }

    return (
      <Fragment>
        <Button
          className={classes.iconCtaButton}
          onClick={handleMenuOpen}
          startIcon={<Icon name="SettingsIcon" className={classes.icon} />}
        >
          {course.textInfo.settingsLabel}
        </Button>
        {renderCourseMenu()}
      </Fragment>
    );
  };

  const renderActions = () => {
    const bookActions = showBookActions ? book?.actions || [] : [];
    const settingsMenuTrigger = renderSettingsMenuTrigger();

    if (!bookActions.length && !settingsMenuTrigger) {
      return null;
    }

    return (
      <div className={classes.unifiedActions}>
        {bookActions.map((action) => renderBookAction(action))}
        {settingsMenuTrigger}
      </div>
    );
  };

  const renderInviteStudents = () => {
    if (!showInviteStudents || !course.inviteStudents) {
      return null;
    }

    return (
      <Button className={classes.inviteStudentsButton} onClick={course.inviteStudents.callback}>
        {course.inviteStudents.text}
      </Button>
    );
  };

  const inviteStudents = renderInviteStudents();

  return (
    <CustomCard className={`${classes.card} ${classes.unifiedContainer}`}>
      <div className={classes.unifiedCard}>
        <div className={classes.unifiedCardMain}>
          <div className={classes.unifiedCardBody}>
            {renderBookCover()}
            <div className={classes.unifiedContent}>
              {showCourseTitle && course.title && (
                <Typography className={classes.unifiedCourseTitle}>{course.title}</Typography>
              )}
              {renderMetaData()}
              {showBookTitle && book.title && (
                <Typography className={classes.unifiedBookTitle}>{book.title}</Typography>
              )}
            </div>
            {renderActions()}
          </div>
          {inviteStudents && (
            <div className={classes.unifiedFooter}>
              {inviteStudents}
            </div>
          )}
        </div>
      </div>
    </CustomCard>
  );
};

UnifiedCourseCard.propTypes = {
  course: PropTypes.object,
  book: PropTypes.object,
  classes: PropTypes.object,
  config: PropTypes.object,
  callback: PropTypes.shape({
    handleMenuOpenChange: PropTypes.func
  })
};

UnifiedCourseCard.defaultProps = {
  course: {},
  book: {},
  classes: {},
  config: {},
  callback: {}
};

export default UnifiedCourseCard;
